import { useEffect, useRef, useState } from 'react';

// Remembering which accordions a reader had open.
//
// Leaving a dashboard and coming back used to reset every goal, strategy and
// panel, so someone following a link out — to the CARP dashboard, or to a
// roadmap action — lost their place and had to re-open everything. These hooks
// keep that state for the browser session.
//
// sessionStorage, not localStorage, on purpose: it is per tab and clears when
// the tab closes, so a reader's next visit starts from the page's own defaults
// rather than from something they expanded weeks ago. It is also per origin, so
// nothing here travels between people or devices.
//
// Storage can be unavailable or full (private windows, blocked site data), and
// then every read and write throws. Both hooks fall back to ordinary state in
// that case: the page works, it just forgets.

const read = (key) => {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
};

const write = (key, value) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — the page still works, it just forgets */
  }
};

// A Set of open item ids, stored as an array. `initial` may be a Set or a
// function returning one, and is used only when nothing is stored yet.
export function usePersistentSet(key, initial = () => new Set()) {
  const [value, setValue] = useState(() => {
    const stored = read(key);
    if (Array.isArray(stored)) return new Set(stored);
    return typeof initial === 'function' ? initial() : initial;
  });

  // Skip the write on the first render: it would only rewrite what was just
  // read, and on a page whose default is "all open" it would persist that
  // default before the reader has touched anything.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    write(key, [...value]);
  }, [key, value]);

  return [value, setValue];
}

// A single expanded/collapsed panel.
export function usePersistentFlag(key, initial = false) {
  const [value, setValue] = useState(() => {
    const stored = read(key);
    return typeof stored === 'boolean' ? stored : initial;
  });

  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    write(key, value);
  }, [key, value]);

  return [value, setValue];
}
